import { hashSeed, mulberry32 } from "./random.js";

const HORIZON = 0.8;
export const STAR_CAP = 500;

const DUST_R = [0.3, 0.65];
const DUST_ALPHA = [0.06, 0.18];
const UNTOUCHED_DUST_R = [0.45, 1.0];
const UNTOUCHED_DUST_ALPHA = [0.16, 0.42];
const EARNED_DIM_R = [0.7, 1.2];
const EARNED_DIM_ALPHA = [0.35, 0.6];
const EARNED_BRIGHT_R = [1.3, 2.1];
const EARNED_BRIGHT_ALPHA = [0.65, 0.9];
const EARNED_BRIGHT_CHANCE = 0.25;

function band(rng, range) {
  return range[0] + rng() * (range[1] - range[0]);
}

const RAMP = ["quiet", "low", "mid", "high", "full"];
const ALPHA_LOW = 0.44;
const ALPHA_HIGH = 0.84;
const REACH_LOW = 0.27;
const REACH_HIGH = 0.43;
const REACH_STAGGER = [1, 0.78, 0.9, 0.7, 0.84];
const ALPHA_STAGGER = [1, 0.78, 0.55, 0.78];

const TIP_MIX = 0.55;

function lerp(a, b, t) {
  return a + (b - a) * t;
}

function clampChannel(value) {
  return Math.max(0, Math.min(255, Math.round(value)));
}

const GRADIENT_SAMPLES = 2;

// Skia interpolates gradient stops linearly, so a slope break at a bare waypoint
// draws a straight mach band across the ribbon. Each segment is resampled with
// smoothstep easing instead, which lands every waypoint with zero slope.
export function veilGradientStops(veil) {
  const peak = Math.min(0.92, veil.alpha * 1.08);
  const waypoints = [
    { at: 0, rgb: veil.rgbTip, alpha: 0 },
    { at: 0.35, rgb: veil.rgb, alpha: veil.alpha * 0.5 },
    { at: 0.62, rgb: veil.rgbCore, alpha: peak },
    { at: 1, rgb: veil.rgbCore, alpha: peak },
  ];
  const colors = [];
  const positions = [];
  for (let i = 0; i < waypoints.length - 1; i++) {
    const from = waypoints[i];
    const to = waypoints[i + 1];
    const fromRgb = from.rgb.split(",").map(Number);
    const toRgb = to.rgb.split(",").map(Number);
    for (let s = 0; s < GRADIENT_SAMPLES; s++) {
      const t = s / GRADIENT_SAMPLES;
      const eased = t * t * (3 - 2 * t);
      const channels = fromRgb.map((channel, k) => clampChannel(lerp(channel, toRgb[k], eased)));
      positions.push(lerp(from.at, to.at, t));
      colors.push(`rgba(${channels.join(",")},${lerp(from.alpha, to.alpha, eased).toFixed(3)})`);
    }
  }
  const last = waypoints[waypoints.length - 1];
  positions.push(last.at);
  colors.push(`rgba(${last.rgb},${last.alpha.toFixed(3)})`);
  return { colors, positions };
}

function warmthRgb(warmth, palette) {
  const t = Math.max(0, Math.min(1, warmth)) * (RAMP.length - 1);
  const i = Math.min(Math.floor(t), RAMP.length - 2);
  const f = t - i;
  const lo = palette.tones[RAMP[i]];
  const hi = palette.tones[RAMP[i + 1]];
  return [lerp(lo[0], hi[0], f), lerp(lo[1], hi[1], f), lerp(lo[2], hi[2], f)];
}

// A one or two week sky is a lone beam that reads better heavily softened, while
// a dense sky needs almost no blur or the overlapping curtain smears to a wash.
export function blurFactor(beamCount) {
  if (beamCount <= 1) return 0.09;
  if (beamCount === 2) return 0.07;
  if (beamCount === 3) return 0.05;
  if (beamCount <= 10) return 0.03;
  return 0.025;
}

export function curtainBlur(beamCount, width, palette) {
  return width * blurFactor(beamCount) * (palette.light?.diffusion ?? 1);
}

// referenceWidth sizes the crown dots. It defaults to width (the phone case), but
// on a wide desktop it is passed the phone-like reference so a crown does not
// scale up with the aurora band and read as a bullet point.
export function buildCurtains(beams, width, height, palette, referenceWidth = width, appearance = {}) {
  const n = beams.length;
  const cw = width / n;
  const horizonY = height * HORIZON;

  const veils = [];
  const crowns = [];
  let warmthTotal = 0;
  for (let i = 0; i < n; i++) {
    if (beams[i].doses === 0) continue;
    const warmth = Math.max(0, Math.min(1, beams[i].warmth));
    warmthTotal += warmth;
    const x = (i + 0.5) * cw;
    const maxHalf = width * 0.2;
    const halfWidth = Math.min(cw * 0.775, maxHalf);
    const random = mulberry32(hashSeed(`${appearance.seed}/beam/${beams[i].key ?? i}`));
    const reach = appearance.seed
      ? lerp(0.2, 0.58, random())
      : lerp(REACH_LOW, REACH_HIGH, warmth) * REACH_STAGGER[i % REACH_STAGGER.length];
    const bottomY = height * 1.05;
    const alpha = lerp(ALPHA_LOW, ALPHA_HIGH, warmth) * ALPHA_STAGGER[i % ALPHA_STAGGER.length];
    const base = warmthRgb(warmth, palette);
    const rgb = base.map((channel) => clampChannel(channel)).join(",");
    const tipMix = palette.beamProfile === "monochrome" ? 0 : TIP_MIX;
    const rgbTip = base.map((channel, k) => clampChannel(channel * (1 - tipMix) + palette.tip[k] * tipMix)).join(",");
    const rgbCore = base
      .map((channel) => clampChannel(channel + (255 - channel) * (palette.light?.coreLift ?? 0.1)))
      .join(",");
    const topY = Math.max(appearance.top ?? 0, horizonY - height * reach);
    veils.push({ x, halfWidth, topY, bottomY, rgb, rgbTip, rgbCore, alpha });

    if (beams[i].crowned) {
      crowns.push({ x, y: topY + height * 0.02, r: 2.4 * (referenceWidth / 540), alpha: 0.9 });
    }
  }

  const glowAlpha = 0.05 + (warmthTotal / Math.max(1, n)) * 0.12;

  return { veils, crowns, glowAlpha, horizonY };
}

export function buildScene(
  beams,
  seed,
  width,
  height,
  palette,
  activityCount,
  referenceWidth = width,
  curtainWidth = referenceWidth,
  starScale = 1,
  beamAppearance = {}
) {
  // Curtains are built in their own band and centered, so a wide desktop shows the
  // same packed, overlapping aurora the phone does rather than a row of isolated
  // bars spread across the screen. curtainWidth sizes that band, referenceWidth
  // sizes the stars, and the star field still fills the whole canvas.
  const curtains = buildCurtains(beams, curtainWidth, height, palette, referenceWidth, beamAppearance);
  const curtainOffsetX = (width - curtainWidth) / 2;
  if (curtainOffsetX !== 0) {
    for (const veil of curtains.veils) veil.x += curtainOffsetX;
    for (const crown of curtains.crowns) crown.x += curtainOffsetX;
  }
  // Star radius tracks the reference width, not the canvas width, so a desktop
  // does not draw the same seeded field as boulders.
  const unit = (referenceWidth / 540) * starScale;

  // One star per logged dose, weigh-in, and photo. Seeded independently of the
  // weeks and append-only, so the field never shifts as weeks accumulate.
  const starRng = mulberry32(hashSeed(`${seed}-stars`));
  const starCount = activityCount == null ? 0 : Math.min(activityCount, STAR_CAP);
  const stars = [];
  for (let s = 0; s < starCount; s++) {
    const bright = starRng() < EARNED_BRIGHT_CHANCE;
    stars.push({
      x: starRng() * width,
      y: Math.pow(starRng(), 1.8) * height * 0.92,
      r: band(starRng, bright ? EARNED_BRIGHT_R : EARNED_DIM_R) * unit,
      alpha: band(starRng, bright ? EARNED_BRIGHT_ALPHA : EARNED_DIM_ALPHA),
    });
  }

  // A faint dusting every sky gets regardless of activity, so a brand new night
  // reads as a night rather than a black screen.
  const ambientRng = mulberry32(hashSeed(`${seed}-ambient`));
  const untouched = stars.length === 0;
  const ambientCount = Math.round((untouched ? 260 : 150) * (height / 960));
  const dustAlpha = untouched ? UNTOUCHED_DUST_ALPHA : DUST_ALPHA;
  const dustR = untouched ? UNTOUCHED_DUST_R : DUST_R;
  const ambientStars = [];
  for (let s = 0; s < ambientCount; s++) {
    ambientStars.push({
      x: ambientRng() * width,
      y: Math.pow(ambientRng(), 1.5) * height,
      r: band(ambientRng, dustR) * unit,
      alpha: band(ambientRng, dustAlpha),
    });
  }

  if (starScale < 1) {
    const layers = buildAmbientLayers(seed, width, height, referenceWidth);
    return { ...curtains, stars, ambientStars: layers.far, nearStars: layers.near };
  }
  return { ...curtains, stars, ambientStars, nearStars: [] };
}

function buildAmbientLayers(seed, width, height, referenceWidth) {
  const unit = (referenceWidth / 540) * 0.75;
  const density = (height / (referenceWidth * (900 / 380))) * (width / referenceWidth);
  const layers = {};
  for (const [key, count, chance, brightRadius, dimRadius, brightAlpha, dimAlpha] of [
    ["far", 130, 0.12, [0.7, 1.4], [0.3, 0.7], [0.35, 0.7], [0.1, 0.28]],
    ["near", 20, 0.5, [1.2, 2.2], [0.7, 1.2], [0.55, 0.9], [0.25, 0.5]],
  ]) {
    const rng = mulberry32(hashSeed(`${seed}-drift-${key}`));
    layers[key] = Array.from({ length: Math.round(count * density) }, () => {
      const bright = rng() < chance;
      return {
        x: rng() * width,
        y: Math.pow(rng(), 1.25) * height,
        r: band(rng, bright ? brightRadius : dimRadius) * unit,
        alpha: band(rng, bright ? brightAlpha : dimAlpha),
      };
    });
  }
  return layers;
}
